import api from '../axios';
import { handleApiError } from '../errorHandler';
import type {
  ActivateAccountRequest,
  ActivateAccountResponse,
  IBridgeCodeRequest,
  IBridgeCodeResponse,
  IValidateSessionPasswordRequest,
  IValidateSessionPasswordResponse,
  ResendActivationRequest,
  ResendActivationResponse,
  ValidateActivationTokenRequest,
  ValidateActivationTokenResponse,
} from './types';

export interface ForgotPasswordRequest {
  email: string;
  domain?: string;
}

export interface ValidateResetTokenRequest {
  token: string;
}

export interface ValidateResetTokenResponse {
  valid: boolean;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export type {
  ActivateAccountRequest,
  ActivateAccountResponse,
  AuthErrorCode,
  IAuthApiError,
  IBridgeCodeRequest,
  IBridgeCodeResponse,
  ILoginResponse,
  ILoginUser,
  IRegisterResponse,
  IValidateSessionPasswordRequest,
  IValidateSessionPasswordResponse,
  ResendActivationRequest,
  ResendActivationResponse,
  ValidateActivationTokenRequest,
  ValidateActivationTokenResponse,
} from './types';

export { parseAuthError, isAuthErrorCode } from './parseAuthError';
export { parseApiError, isApiErrorCode } from './parseApiError';
export {
  clearCsrfCache,
  clearSessionLoggedOut,
  fetchCurrentUser,
  fetchCsrfToken,
  getCsrfToken,
  hasSessionCredentials,
  logoutSession,
  markSessionLoggedOut,
  readXsrfCookie,
  refreshSession,
} from './session';

/** Valida a senha do usuário autenticado (sem email no body). */
export const validateSessionPassword = async (
  password: string
): Promise<IValidateSessionPasswordResponse> => {
  try {
    const payload: IValidateSessionPasswordRequest = { password };
    const response = await api.post<IValidateSessionPasswordResponse>(
      '/auth/validate-password',
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao validar senha da sessão:', error);
    throw handleApiError(error);
  }
};

/**
 * Solicita o envio de um link de recuperação de senha
 */
export const forgotPassword = async (data: ForgotPasswordRequest): Promise<void> => {
  try {
    await api.post('/auth/forgot-password', data);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Valida se o token de redefinição de senha é válido
 */
export const validateResetToken = async (
  data: ValidateResetTokenRequest
): Promise<ValidateResetTokenResponse> => {
  try {
    const response = await api.post('/auth/validate-reset-token', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Redefine a senha usando o token
 */
export const resetPassword = async (data: ResetPasswordRequest): Promise<void> => {
  try {
    await api.post('/auth/reset-password', data);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const validateActivationToken = async (
  data: ValidateActivationTokenRequest
): Promise<ValidateActivationTokenResponse> => {
  try {
    const response = await api.post<ValidateActivationTokenResponse>(
      '/auth/validate-activation-token',
      data
    );
    return response.data;
  } catch (error) {
    console.error('Error validating activation token:', error);
    throw handleApiError(error);
  }
};

export const activateAccount = async (
  data: ActivateAccountRequest
): Promise<ActivateAccountResponse> => {
  try {
    const response = await api.post<ActivateAccountResponse>('/auth/activate-account', data);
    return response.data;
  } catch (error) {
    console.error('Error activating account:', error);
    throw handleApiError(error);
  }
};

export const resendActivation = async (
  data: ResendActivationRequest
): Promise<ResendActivationResponse> => {
  try {
    const response = await api.post<ResendActivationResponse>('/auth/resend-activation', data);
    return response.data;
  } catch (error) {
    console.error('Error resending activation email:', error);
    throw handleApiError(error);
  }
};

/** Emite bridge code one-shot para redirecionamento SSO ao WordPress. */
export const createAuthBridgeCode = async (
  payload: IBridgeCodeRequest
): Promise<IBridgeCodeResponse> => {
  try {
    const response = await api.post<IBridgeCodeResponse>('/auth/bridge-code', payload);
    return response.data;
  } catch (error) {
    console.error('Erro ao gerar bridge code:', error);
    throw handleApiError(error);
  }
};
